import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Trash2, User, Zap, Flame, BookOpen, ShieldCheck, Users, Trophy,
  ClipboardList, Check, Loader2, Settings2, Mail, ArrowRightLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { AddPlayerModal } from './AddPlayerModal';
import { usePlayerProfiles, type PlayerWithProgress } from '@/hooks/usePlayerProfiles';
import { useChildDashboard } from '@/hooks/useChildDashboard';
import { getLevelName } from '@/hooks/useProgress';
import { achievements as ACHIEVEMENTS } from '@/data/achievements';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { switchToChildAccount } from '@/lib/switchToChild';
import { FaqSection } from '@/components/FaqSection';

const SPORT_EMOJI: Record<string, string> = {
  football: '🏈', soccer: '⚽', basketball: '🏀', baseball: '⚾',
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function SafetyNote() {
  return (
    <p className="text-[12px] text-muted-foreground font-body flex items-center gap-1.5">
      <ShieldCheck className="w-3.5 h-3.5 text-parent shrink-0" />
      Your child's data is safe — we never sell personal information
    </p>
  );
}

function StatTile({ icon: Icon, label, value }: { icon: typeof Zap; label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border-2 border-border bg-card/60 p-3 text-center">
      <Icon className="w-4 h-4 text-parent mx-auto" />
      <p className="font-display font-bold text-lg mt-1">{value}</p>
      <p className="text-[11px] text-muted-foreground font-body">{label}</p>
    </div>
  );
}

/** Weekly snapshot — lessons completed per day, derived from recent activity. */
function WeeklyChart({ streak, lessonsThisWeek }: { streak: number; lessonsThisWeek: number[] }) {
  const max = Math.max(1, ...lessonsThisWeek);
  return (
    <div className="rounded-2xl border-2 border-border bg-card/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-sm">This week</h3>
        <span className="text-[11px] text-muted-foreground font-body">{streak} day streak 🔥</span>
      </div>
      <div className="flex items-end justify-between gap-2 h-24">
        {lessonsThisWeek.map((n, i) => (
          <div key={DAYS[i]} className="flex-1 flex flex-col items-center gap-1">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${(n / max) * 100}%` }}
              className={`w-full rounded-t-md ${n > 0 ? 'bg-parent' : 'bg-muted'}`}
              style={{ minHeight: 4 }}
            />
            <span className="text-[10px] text-muted-foreground font-body">{DAYS[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChildDetail({ child }: { child: PlayerWithProgress }) {
  const { data, team, prefs, loading, savePrefs, joinTeam } = useChildDashboard(child.player_user_id);
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Approximate weekly activity from the child's streak (per-day history is
  // not stored yet — the streak tells us which recent days were active).
  const weekBars = useMemo(() => {
    const today = (new Date().getDay() + 6) % 7; // Mon = 0
    return DAYS.map((_, i) => {
      const daysAgo = today - i;
      if (daysAgo < 0) return 0;
      return daysAgo < (data?.streak ?? 0) ? 1 : 0;
    });
  }, [data?.streak]);

  // Hands the phone over to the child: signs the parent out and opens the
  // player login with the child's username already filled in.
  const switchToChild = async () => {
    toast.info(`Enter ${child.display_name || child.username}'s PIN to continue.`);
    await switchToChildAccount(child.username);
  };

  const handleJoin = async () => {
    setJoining(true);
    const res = await joinTeam(code);
    setJoining(false);
    if ((res as any)?.error) toast.error((res as any).error);
    else { toast.success(`${child.display_name || child.username} joined ${(res as any).team_name}!`); setCode(''); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-parent" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Activity summary */}
      {(data?.xp ?? 0) === 0 && (data?.completed_lessons?.length ?? 0) === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-parent/40 bg-parent/5 p-4">
          <p className="font-display font-bold text-sm">
            {child.display_name || child.username} hasn't started yet
          </p>
          <p className="text-xs text-muted-foreground font-body mt-1">
            Their first lesson takes about 5 minutes — XP, streak, and badges will show up here
            as soon as they finish it.
          </p>
        </div>
      )}
      <div className="grid grid-cols-4 gap-2">
        <StatTile icon={Flame} label="Streak" value={data?.streak ?? 0} />
        <StatTile icon={Zap} label="XP" value={data?.xp ?? 0} />
        <StatTile icon={BookOpen} label="Lessons" value={data?.completed_lessons?.length ?? 0} />
        <StatTile icon={Trophy} label="Level" value={getLevelName(data?.level ?? 0)} />
      </div>

      {/* Join a team — stays visible until a team is added */}
      {!team && (
        <div className="rounded-2xl border-2 border-parent/40 bg-parent/5 p-4 space-y-3">
          <div>
            <h3 className="font-display font-bold text-base">Join a Team</h3>
            <p className="text-sm text-muted-foreground font-body">
              Got a Team Code from a coach? Add it to connect {child.display_name || child.username}.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={e => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12))}
              placeholder="TEAM CODE"
              className="h-11 rounded-xl border-2 font-display font-bold tracking-[0.2em] uppercase"
            />
            <Button
              onClick={handleJoin} disabled={code.length < 4 || joining}
              className="h-11 rounded-xl font-display font-bold bg-parent text-parent-foreground hover:bg-parent/90"
            >
              {joining ? '...' : 'Join'}
            </Button>
          </div>
        </div>
      )}

      {/* Coach card */}
      {team && (
        <div className="rounded-2xl border-2 border-border bg-card/60 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-coach/15 flex items-center justify-center shrink-0">
              <ClipboardList className="w-5 h-5 text-coach" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm truncate">{team.team_name}</p>
              <p className="text-xs text-muted-foreground font-body truncate">Coach {team.coach_name}</p>
            </div>
            <Button
              variant="outline" size="sm"
              className="h-9 rounded-xl font-display font-bold gap-1.5"
              onClick={() => toast.info('Coach messaging is coming soon — for now, reach out through your club.')}
            >
              <Mail className="w-3.5 h-3.5" /> Message
            </Button>
          </div>
        </div>
      )}

      {/* Coach assigned content */}
      <div className="rounded-2xl border-2 border-border bg-card/60 p-4">
        <h3 className="font-display font-bold text-sm mb-2">Coach assigned content</h3>
        {(data?.coach_content?.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground font-body">Nothing assigned yet.</p>
        ) : (
          <div className="space-y-2">
            {data!.coach_content.slice(0, 6).map(c => (
              <div key={c.id} className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                  c.viewed ? 'bg-parent/20 text-parent' : 'bg-muted text-muted-foreground'
                }`}>
                  {c.viewed ? <Check className="w-3 h-3" /> : <span className="text-[10px]">•</span>}
                </span>
                <p className="text-sm font-body truncate flex-1">{c.title}</p>
                <span className="text-[11px] text-muted-foreground font-body shrink-0">
                  {c.viewed ? 'Viewed' : 'Not viewed'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <WeeklyChart streak={data?.streak ?? 0} lessonsThisWeek={weekBars} />

      {/* Recent milestones */}
      <div className="rounded-2xl border-2 border-border bg-card/60 p-4">
        <h3 className="font-display font-bold text-sm mb-2">Recent milestones</h3>
        {(data?.achievements?.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground font-body">
            No badges yet — they'll appear here as {child.display_name || child.username} levels up.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {data!.achievements.map(a => {
              const meta = (ACHIEVEMENTS as any[]).find(x => x.id === a.achievement_id);
              return (
                <span key={a.achievement_id} className="text-xs font-body px-3 py-1.5 rounded-full bg-parent/10 text-parent">
                  {meta?.emoji ?? '🏅'} {meta?.title ?? a.achievement_id}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick settings */}
      <div className="rounded-2xl border-2 border-border bg-card/60 p-4">
        <button
          onClick={() => setShowSettings(v => !v)}
          className="w-full flex items-center justify-between"
        >
          <span className="font-display font-bold text-sm flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-parent" /> Quick settings
          </span>
          <span className="text-xs text-muted-foreground font-body">{showSettings ? 'Hide' : 'Show'}</span>
        </button>

        {showSettings && (
          <div className="mt-3 space-y-2">
            {([
              ['weekly_report', 'Weekly progress report email'],
              ['coach_content', 'New coach content notifications'],
              ['streak_reminders', 'Streak reminder notifications'],
              ['milestone_alerts', 'Milestone and achievement alerts'],
            ] as const).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2.5">
                <p className="font-body text-sm">{label}</p>
                <Switch
                  checked={prefs[key]}
                  onCheckedChange={v => savePrefs({ ...prefs, [key]: v })}
                  aria-label={label}
                />
              </div>
            ))}
            {team && (
              <p className="text-[11px] text-muted-foreground font-body pt-1">
                Team: {team.team_name} · Coach {team.coach_name}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3 space-y-3">
        <p className="text-xs text-muted-foreground font-body flex items-center gap-2">
          <ArrowRightLeft className="w-3.5 h-3.5 shrink-0" />
          {child.display_name || child.username} can log in on their own device from the
          "Player" tab with <span className="font-bold text-foreground">@{child.username}</span> + their PIN.
        </p>
        <Button
          variant="outline"
          onClick={switchToChild}
          className="w-full h-11 rounded-xl font-display font-bold border-2 gap-2"
        >
          <ArrowRightLeft className="w-4 h-4" />
          Switch to {child.display_name || child.username}'s Account
        </Button>
      </div>
    </div>
  );
}

export function ParentDashboard() {
  const { players, createPlayer, deletePlayer } = usePlayerProfiles();
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedId && players.length > 0) setSelectedId(players[0].id);
    if (selectedId && !players.some(p => p.id === selectedId)) setSelectedId(players[0]?.id ?? null);
  }, [players, selectedId]);

  const selected = players.find(p => p.id === selectedId) ?? null;

  const handleDelete = async (player: PlayerWithProgress) => {
    try {
      await deletePlayer(player.id);
      toast.success(`${player.display_name || player.username} removed`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove player');
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-xl">Parent Dashboard</h2>
          <p className="text-sm text-muted-foreground font-body">{players.length}/3 child accounts</p>
        </div>
        {players.length < 3 && (
          <Button
            onClick={() => setShowAddModal(true)}
            size="sm"
            className="h-9 font-display font-bold rounded-xl gap-1.5 bg-parent text-parent-foreground hover:bg-parent/90"
          >
            <Plus className="w-4 h-4" />
            Add Another Child
          </Button>
        )}
      </div>

      <SafetyNote />

      {players.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-2xl border-2 border-dashed border-border p-8 text-center"
        >
          <div className="w-14 h-14 rounded-full bg-parent/10 flex items-center justify-center mx-auto mb-3">
            <User className="w-7 h-7 text-parent" />
          </div>
          <h3 className="font-display font-bold text-lg">No children added yet</h3>
          <p className="text-sm text-muted-foreground font-body mt-1 mb-4">
            Add up to 3 children. Each gets their own login and personalized lessons.
          </p>
          <Button
            onClick={() => setShowAddModal(true)}
            className="h-11 font-display font-bold rounded-xl gap-2 bg-parent text-parent-foreground hover:bg-parent/90"
          >
            <Plus className="w-4 h-4" />
            Add First Child
          </Button>
        </motion.div>
      ) : (
        <>
          {/* Child selector — only when more than one child exists */}
          {players.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {players.map(p => (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-xl border-2 transition-all ${
                    selectedId === p.id ? 'border-parent bg-parent/10' : 'border-border text-muted-foreground'
                  }`}
                >
                  <Users className="w-3.5 h-3.5" />
                  <span className="font-display font-bold text-sm">{p.display_name || p.username}</span>
                  <span className="text-sm">{SPORT_EMOJI[p.sport] || '🏈'}</span>
                </button>
              ))}
            </div>
          )}

          {selected && (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-lg truncate">
                    {selected.display_name || selected.username} {SPORT_EMOJI[selected.sport] || '🏈'}
                  </h3>
                  <p className="text-xs text-muted-foreground font-body">
                    @{selected.username} · {selected.skill_level || 'beginner'}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(selected)}
                  className="text-muted-foreground hover:text-destructive transition-colors p-1 shrink-0"
                  aria-label="Remove child"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <ChildDetail key={selected.id} child={selected} />
            </>
          )}
        </>
      )}

      {/* Parent FAQ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="bg-card rounded-2xl border-2 border-border p-5 shadow-card"
      >
        <FaqSection
          title="Parent FAQ"
          items={[
            { id: 'add-player', question: 'How do I add another child?', answer: 'Tap "Add Another Child" and enter their first name, username, sport, and a 4-digit PIN. Each child gets their own login and personalized lesson experience. You can add up to 3 children.' },
            { id: 'player-login', question: 'How does my child log in?', answer: 'On the login screen, your child taps "Player," then enters their username and 4-digit PIN. They see their own lessons, progress, and XP — exactly the same home screen you see when you preview their account.' },
            { id: 'track-progress', question: 'Can I see my child\'s progress?', answer: 'Yes — this dashboard shows their streak, XP, lessons completed, weekly activity, coach-assigned content, and any badges they have earned.' },
            { id: 'team-code', question: 'What if I do not have a team code yet?', answer: 'You can skip it during signup. The "Join a Team" card stays on this dashboard until you add a code from your coach.' },
            { id: 'remove-player', question: 'What happens if I remove a child?', answer: 'Removing a child deletes their profile and progress permanently and they can no longer log in. This cannot be undone.' },
            { id: 'safe-content', question: 'Is the content safe for kids?', answer: 'Absolutely. All lessons are educational sports content focused on rules, strategy, and game knowledge. There is no social messaging, advertising, or inappropriate content, and we never sell personal information.' },
          ]}
        />
      </motion.div>

      <AddPlayerModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreate={createPlayer}
        currentCount={players.length}
      />
    </div>
  );
}
